import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Button, showToast } from '@attentive-platform/stem-ui';

import { FormContainer, WideCard } from '../../styled';
import { apiErrorMessage } from '../../Utils/apiErrors';
import { CompanyPicker } from '../SharedComponent';
import CredentialModal from './CredentialModal';
import CredentialsTable from './CredentialsTable';
import RemoveCredentialModal from './RemoveCredentialModal';
import { deletePlanroom, listPlanrooms, parsePlanrooms, planroomsCount, PLANROOMS_PER_PAGE } from './api';
import { CompanyFieldLabel } from './styles';

const PlanRoomCredentials = () => {
  const [company, setCompany] = useState(null);
  const [loadedCompany, setLoadedCompany] = useState(null);
  const [planrooms, setPlanrooms] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [pickerKey, setPickerKey] = useState(0);
  const requestRef = useRef(null);
  const selectedCompanies = useMemo(() => (company ? [company] : []), [company]);
  const pageCount = Math.max(1, Math.ceil(totalCount / PLANROOMS_PER_PAGE));
  const handleSearchError = useCallback(message => showToast(message, 'error'), []);

  const cancelRequest = () => {
    requestRef.current?.abort();
    requestRef.current = null;
    setLoading(false);
  };

  const loadPage = async (target, nextPage) => {
    if (!target?.id) return;
    const controller = new AbortController();
    requestRef.current?.abort();
    requestRef.current = controller;
    setLoading(true);
    try {
      const { data, error } = await listPlanrooms({
        companyId: target.id,
        page: nextPage,
        signal: controller.signal
      });
      if (controller.signal.aborted) return;
      if (error) {
        showToast(apiErrorMessage(error, 'Could not load planrooms'), 'error');
        return;
      }
      const rows = parsePlanrooms(data);
      const count = planroomsCount(data);
      const lastPage = Math.max(1, Math.ceil(count / PLANROOMS_PER_PAGE));
      if (!rows.length && nextPage > lastPage) {
        requestRef.current = null;
        await loadPage(target, lastPage);
        return;
      }
      setPlanrooms(rows);
      setTotalCount(count);
      setPage(nextPage);
      setLoadedCompany(target);
    } catch {
      if (!controller.signal.aborted) showToast('Could not load planrooms', 'error');
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleCompanyChange = companies => {
    const next = companies[0] || null;
    setCompany(next);
    if (next?.id !== loadedCompany?.id) {
      cancelRequest();
      setPlanrooms(null);
      setLoadedCompany(null);
      setPage(1);
      setTotalCount(0);
    }
  };

  const handleProceed = () => {
    if (!company?.id || loading) return;
    loadPage(company, 1);
  };

  const handlePageChange = (_, nextPage) => {
    if (!loadedCompany || nextPage === page) return;
    loadPage(loadedCompany, nextPage);
  };

  const applySaved = () => {
    const wasEdit = Boolean(editing?.id);
    setAddOpen(false);
    setEditing(null);
    loadPage(loadedCompany, wasEdit ? page : 1);
  };

  const handleReset = () => {
    cancelRequest();
    setPickerKey(key => key + 1);
    setCompany(null);
    setLoadedCompany(null);
    setPlanrooms(null);
    setPage(1);
    setTotalCount(0);
    setAddOpen(false);
    setEditing(null);
    setPendingRemoval(null);
  };

  const handleCompanyAction = loadedCompany ? handleReset : handleProceed;
  const openAdd = () => setAddOpen(true);
  const closeModal = () => {
    setAddOpen(false);
    setEditing(null);
  };

  const handleRemove = planroom => {
    if (!planroom?.id || removingId) return;
    setPendingRemoval(planroom);
  };

  const closeRemoval = () => setPendingRemoval(null);

  const confirmRemove = async () => {
    const planroom = pendingRemoval;
    if (!planroom?.id || removingId) return;
    setRemovingId(planroom.id);
    try {
      const { error } = await deletePlanroom({ planroomId: planroom.id });
      if (error) {
        showToast(apiErrorMessage(error, 'Could not remove credential'), 'error');
        return;
      }
      showToast('Credential removed', 'success');
      setPendingRemoval(null);
      loadPage(loadedCompany, page);
    } catch {
      showToast('Could not remove credential', 'error');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <FormContainer>
      <WideCard className='rounded-xl border border-gray-200 bg-white p-6 text-left shadow-sm'>
        <h2 className='m-0 text-xl font-semibold text-gray-900'>PlanRoom Credentials</h2>
        <CompanyFieldLabel>Company</CompanyFieldLabel>
        <div className='flex items-start gap-3'>
          <div className='min-w-0 flex-1'>
            <CompanyPicker
              key={pickerKey}
              multiple={false}
              disabled={Boolean(loadedCompany) || loading}
              selected={selectedCompanies}
              onChange={handleCompanyChange}
              onError={handleSearchError}
            />
          </div>
          <Button
            color='primary'
            disabled={!loadedCompany && (!company || loading)}
            loading={loading && !loadedCompany}
            onClick={handleCompanyAction}>
            {loadedCompany ? 'Reset' : 'Proceed'}
          </Button>
        </div>
        {planrooms && (
          <CredentialsTable
            companyName={loadedCompany?.name || company?.name}
            planrooms={planrooms}
            loading={loading}
            removingId={removingId}
            page={page}
            pageCount={pageCount}
            onPageChange={handlePageChange}
            onAdd={openAdd}
            onEdit={setEditing}
            onRemove={handleRemove}
          />
        )}
        {(addOpen || editing) && (
          <CredentialModal
            open
            company={loadedCompany || company}
            planroom={editing}
            onClose={closeModal}
            onSaved={applySaved}
          />
        )}
        {pendingRemoval && (
          <RemoveCredentialModal
            planroom={pendingRemoval}
            loading={removingId === pendingRemoval.id}
            onClose={closeRemoval}
            onConfirm={confirmRemove}
          />
        )}
      </WideCard>
    </FormContainer>
  );
};

export default PlanRoomCredentials;
